import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Truck, Package, CheckCircle, Clock } from 'lucide-react';
import { fetchDeliveries } from '../services/api';

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDeliveries();
  }, []);

  useEffect(() => {
    filterDeliveries();
  }, [deliveries, searchTerm, statusFilter]);

  const loadDeliveries = async () => {
    try {
      const data = await fetchDeliveries();
      setDeliveries(data);
    } catch (error) {
      console.error('Failed to load deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDeliveries = () => {
    let filtered = deliveries;

    if (searchTerm) {
      filtered = filtered.filter(delivery =>
        delivery.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.order_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(delivery => delivery.status === statusFilter);
    }

    setFilteredDeliveries(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_transit':
        return <Truck className="text-blue-500" size={20} />;
      case 'delivered':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'pending':
        return <Clock className="text-yellow-500" size={20} />;
      default:
        return <Package className="text-gray-500" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_transit: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading deliveries...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Deliveries</h1>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by tracking or order number..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="input-field"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Deliveries List */}
      <div className="space-y-4">
        {filteredDeliveries.length === 0 ? (
          <div className="card text-center py-12 text-gray-500">
            No deliveries found.
          </div>
        ) : (
          filteredDeliveries.map((delivery) => (
            <div key={delivery.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3 md:gap-4 flex-1">
                  <div className="bg-gray-100 p-3 rounded-lg flex-shrink-0">
                    {getStatusIcon(delivery.status)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <Link
                        to={`/orders/${delivery.order_id}`}
                        className="font-semibold text-primary-600 hover:underline"
                      >
                        Order #{delivery.order_number}
                      </Link>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(delivery.status)} w-fit`}>
                        {delivery.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 break-all sm:break-normal">
                      Tracking: <span className="font-mono">{delivery.tracking_number}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {delivery.customer_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {delivery.shipping_address?.city}, {delivery.shipping_address?.state}
                    </p>
                  </div>
                </div>

                <div className="md:text-right md:ml-4 pl-11 md:pl-0">
                  <p className="text-sm text-gray-600">Carrier</p>
                  <p className="font-medium">{delivery.carrier}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Est. Delivery: {new Date(delivery.estimated_delivery).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {delivery.status === 'in_transit' && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 text-sm">
                    <span className="text-gray-600">Last Update:</span>
                    <span className="sm:text-right">{delivery.last_update || 'No updates yet'}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Deliveries;
